import { Fragment, useEffect, useMemo, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import {
  fetchHeroProfile,
  fetchMeGroups,
  getUserDisplayName,
  getUserLocalLogin,
  logout,
  normalizeGroupLabel,
  type CurrentUser,
  type HeroProfile,
} from "@/lib/auth";
import { useBirthday } from "@/features/birthday/BirthdayProvider";
import { useVacation } from "@/features/vacation/VacationProvider";
import { useDecoPreference } from "@/features/deco/DecoPreferenceProvider";
import { decoAllowsBirthdayAvatarBadge, isDecoSeasonActive } from "@/lib/decoPreference";
import { useUserPhoto } from "@/hooks/useUserPhoto";
import { DecoPreferenceControl } from "@/components/DecoPreferenceControl";
import { NewDesignToggle } from "@/components/NewDesignToggle";
import { FullWidthToggle } from "@/components/FullWidthToggle";
import { HiddenSectionsControl } from "@/components/HiddenSectionsControl";
import { DesktopAppDownload } from "@/components/DesktopAppDownload";
import { isElectronRuntime, openElectronNewWindow } from "@/lib/electron";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { PersonalUploadDialog } from "@/components/PersonalUploadDialog";

const HERO_PARTNER_URL = "https://login.hero-software.de/partner/Account/overview";
const MS_PROFILE_URL = "https://myaccount.microsoft.com/";

interface AccountMenuProps {
  user: CurrentUser | null;
}

export function AccountMenu({ user }: AccountMenuProps) {
  const displayName = getUserDisplayName(user) || "Benutzer";
  const localLogin = getUserLocalLogin(user);
  const email = (user?.email as string | undefined) || (user?.upn as string | undefined) || null;
  const photoUrl = useUserPhoto(localLogin, email);

  const [groups, setGroups] = useState<string[]>(() =>
    ((user?.groups as string[] | undefined) ?? []).map(normalizeGroupLabel).filter(Boolean),
  );
  const [heroProfile, setHeroProfile] = useState<HeroProfile | null>(null);
  const [heroLoading, setHeroLoading] = useState(false);
  const [heroRequested, setHeroRequested] = useState(false);
  const { data: birthdayData, mineIsBirthday: isBirthdayToday } = useBirthday();
  const { vacationActive } = useVacation();
  const { preference: decoPreference } = useDecoPreference();
  const showAvatarBirthdayBadge =
    isBirthdayToday && decoAllowsBirthdayAvatarBadge(decoPreference);
  const showDecoSettings = isDecoSeasonActive(user, isBirthdayToday, vacationActive);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [personalUploadOpen, setPersonalUploadOpen] = useState(false);

  const initials = useMemo(() => {
    const ch = displayName.trim()[0];
    return ch ? ch.toUpperCase() : "?";
  }, [displayName]);

  useEffect(() => {
    setGroups(((user?.groups as string[] | undefined) ?? []).map(normalizeGroupLabel).filter(Boolean));
  }, [user]);

  useEffect(() => {
    if (birthdayData?.me) setHeroProfile(birthdayData.me);
  }, [birthdayData?.me]);

  useEffect(() => {
    if (!heroRequested || heroProfile !== null) return;
    setHeroLoading(true);
    void fetchHeroProfile()
      .then(setHeroProfile)
      .finally(() => setHeroLoading(false));
  }, [heroRequested, heroProfile]);

  function onMenuOpen() {
    void fetchMeGroups()
      .then((apiGroups) => {
        if (apiGroups.length) setGroups(apiGroups);
      })
      .catch(() => {});
    setHeroRequested(true);
  }

  if (!user) return null;

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <Menu.Button
            className={clsx(
              "inline-flex max-w-[min(100%,14rem)] items-center gap-2 rounded-full py-1 pl-1 pr-2.5",
              "text-label transition hover:bg-fill-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              open && "bg-fill-tertiary",
            )}
            aria-haspopup="menu"
            onClick={() => {
              if (!open) onMenuOpen();
            }}
          >
            <UserAvatar photoUrl={photoUrl} initials={initials} size="sm" showBirthday={showAvatarBirthdayBadge} />
            <span className="hidden truncate text-sm font-semibold sm:inline md:max-w-[12rem]">
              {displayName}
            </span>
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Menu.Items className="eh-account-menu absolute right-0 z-[100] mt-2 w-[min(92vw,22.5rem)] origin-top-right focus:outline-none">
              <header className="eh-account-header">
                <UserAvatar photoUrl={photoUrl} initials={initials} size="lg" showBirthday={showAvatarBirthdayBadge} />
                <div className="min-w-0">
                  <div className="truncate text-base font-bold text-label">{displayName}</div>
                  <div className="truncate text-sm text-label-secondary">@{localLogin || "–"}</div>
                </div>
              </header>

              <section className="eh-account-section">
                <h3 className="eh-account-section-title">Gruppen</h3>
                {groups.length ? (
                  <ul className="eh-account-group-list">
                    {groups.map((group) => (
                      <li key={group}>{group}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-label-tertiary">Keine Gruppen</p>
                )}
              </section>

              {showDecoSettings ? <DecoPreferenceControl /> : null}

              <NewDesignToggle variant="menu" />
              <FullWidthToggle variant="menu" />
              <HiddenSectionsControl />

              {(heroLoading || heroProfile) && (
                <section className="eh-account-section">
                  <h3 className="eh-account-section-title">Mitarbeiterprofil (Hero)</h3>
                  {heroLoading && !heroProfile ? (
                    <p className="text-sm text-label-tertiary">Lade …</p>
                  ) : heroProfile ? (
                    <dl className="eh-account-hero-grid">
                      <HeroRow label="Lizenz" value={heroProfile.account_type} />
                      <HeroRow label="Telefon" value={heroProfile.phone} href={telHref(heroProfile.phone)} />
                      <HeroRow label="Mobil" value={heroProfile.mobile} href={telHref(heroProfile.mobile)} />
                      <HeroRow label="Geburtstag" value={formatBirthday(heroProfile.birth_date)} />
                    </dl>
                  ) : null}
                </section>
              )}

              <div className="eh-account-actions">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <a
                    href={MS_PROFILE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-accent hover:underline"
                  >
                    Microsoft&nbsp;365‑Profil
                  </a>
                  {heroProfile ? (
                    <a
                      href={HERO_PARTNER_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-accent hover:underline"
                    >
                      Hero Partner
                    </a>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <DesktopAppDownload user={user} variant="menu" />
                  {isDesktop ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setPersonalUploadOpen(true)}
                    >
                      Handy → PC
                    </Button>
                  ) : null}
                  {isElectronRuntime() ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void openElectronNewWindow()}
                    >
                      Neues Fenster
                    </Button>
                  ) : null}
                  <Button type="button" variant="secondary" size="sm" onClick={() => void logout()}>
                    Abmelden
                  </Button>
                </div>
              </div>
            </Menu.Items>
          </Transition>
          <PersonalUploadDialog
            open={personalUploadOpen}
            onClose={() => setPersonalUploadOpen(false)}
          />
        </>
      )}
    </Menu>
  );
}

function UserAvatar({
  photoUrl,
  initials,
  size,
  showBirthday,
}: {
  photoUrl: string | null;
  initials: string;
  size: "sm" | "lg";
  showBirthday?: boolean;
}) {
  const dim = size === "lg" ? "h-14 w-14 text-lg" : "h-8 w-8 text-xs";
  return (
    <span className={clsx("relative inline-flex shrink-0", dim)}>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt=""
          className={clsx("rounded-full object-cover ring-1 ring-separator", dim)}
          width={size === "lg" ? 56 : 32}
          height={size === "lg" ? 56 : 32}
        />
      ) : (
        <span
          className={clsx(
            "inline-grid place-items-center rounded-full bg-accent font-bold text-label-on-accent ring-1 ring-separator",
            dim,
          )}
          aria-hidden
        >
          {initials}
        </span>
      )}
      {showBirthday ? (
        <span className="eh-account-birthday-badge" title="Alles Gute zum Geburtstag!" aria-hidden>
          🎂
        </span>
      ) : null}
    </span>
  );
}

function HeroRow({
  label,
  value,
  href,
}: {
  label: string;
  value?: string | null;
  href?: string | null;
}) {
  const text = value?.trim() || "–";
  return (
    <div className="eh-account-hero-row">
      <dt className="text-label-tertiary">{label}</dt>
      <dd className="min-w-0 truncate font-medium text-label">
        {href && text !== "–" ? (
          <a href={href} className="text-accent hover:underline">
            {text}
          </a>
        ) : (
          text
        )}
      </dd>
    </div>
  );
}

function telHref(value?: string | null): string | null {
  const raw = String(value || "").trim();
  if (!raw || raw === "–") return null;
  return `tel:${raw.replace(/\s+/g, "")}`;
}

function formatBirthday(iso?: string | null): string | null {
  if (!iso) return null;
  const parts = String(iso).split("-").map(Number);
  if (parts.length < 3) return null;
  const [y, m, d] = parts;
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
